// ============================================================
// HipWear — SaveDialog
// Custom HTML/CSS modal dialogs for the Excel save flow.
// All methods return Promises so callers can await them cleanly.
// ============================================================

// ---------------------------------------------------------------------------
// Styles (injected once into <head>)
// ---------------------------------------------------------------------------

const DIALOG_CSS = `
.gw-overlay {
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 10000;
  display: flex; align-items: center; justify-content: center;
}
.gw-dialog {
  background: #1e1e1e;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 24px 28px;
  min-width: 340px;
  max-width: 500px;
  width: 90vw;
  color: #d4d4d4;
  font-family: system-ui, sans-serif;
  font-size: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.7);
}
.gw-dialog h3 {
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
}
.gw-dialog p {
  margin: 0 0 20px;
  line-height: 1.55;
  color: #b0b0b0;
  white-space: pre-line;
  max-height: 60vh;
  overflow-y: auto;
}
.gw-dialog input[type="text"] {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  background: #2a2a2a;
  border: 1px solid #555;
  border-radius: 4px;
  color: #d4d4d4;
  font-size: 14px;
  margin-bottom: 20px;
  outline: none;
}
.gw-dialog input[type="text"]:focus {
  border-color: #0078d4;
}
.gw-btns {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.gw-btn {
  padding: 7px 18px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: opacity 0.15s;
}
.gw-btn:hover { opacity: 0.82; }
.gw-btn.primary   { background: #0078d4; color: #fff; }
.gw-btn.secondary { background: #3a3a3a; color: #ccc; border: 1px solid #555; }
.gw-btn.danger    { background: #c42b1c; color: #fff; }
.gw-field { display: grid; grid-template-columns: 1fr 190px; align-items: center; gap: 10px; margin-bottom: 12px; }
.gw-field label { color: #d4d4d4; }
.gw-field input[type="number"], .gw-field select {
  width: 100%; box-sizing: border-box; padding: 7px 9px; background: #2a2a2a; border: 1px solid #555;
  border-radius: 4px; color: #d4d4d4; font-size: 14px; outline: none;
}
.gw-field input[type="number"]:focus, .gw-field select:focus { border-color: #0078d4; }
.gw-note { margin: -4px 0 16px; color: #8a8a8a; font-size: 12px; line-height: 1.45; }
`;

function injectStyles(): void {
  if (document.getElementById('gw-dialog-styles')) return;
  const style = document.createElement('style');
  style.id = 'gw-dialog-styles';
  style.textContent = DIALOG_CSS;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Low-level modal builder
// ---------------------------------------------------------------------------

interface ButtonDef {
  label: string;
  value: string;
  style: 'primary' | 'secondary' | 'danger';
}

interface ModalResult {
  choice: string;
  inputValue?: string;
}

function showModal(
  title: string,
  message: string,
  buttons: ButtonDef[],
  inputField?: { placeholder: string; defaultValue: string },
): Promise<ModalResult> {
  injectStyles();

  return new Promise((resolve) => {
    // -- Overlay --
    const overlay = document.createElement('div');
    overlay.className = 'gw-overlay';

    // -- Dialog box --
    const dialog = document.createElement('div');
    dialog.className = 'gw-dialog';

    const h3 = document.createElement('h3');
    h3.textContent = title;
    dialog.appendChild(h3);

    const p = document.createElement('p');
    p.textContent = message;
    dialog.appendChild(p);

    // Optional text input
    let input: HTMLInputElement | null = null;
    if (inputField) {
      input = document.createElement('input');
      input.type = 'text';
      input.placeholder = inputField.placeholder;
      input.value = inputField.defaultValue;
      dialog.appendChild(input);
    }

    // Buttons
    const btnRow = document.createElement('div');
    btnRow.className = 'gw-btns';

    const close = (choice: string) => {
      document.body.removeChild(overlay);
      resolve({ choice, inputValue: input?.value });
    };

    for (const def of buttons) {
      const btn = document.createElement('button');
      btn.className = `gw-btn ${def.style}`;
      btn.textContent = def.label;
      btn.onclick = () => close(def.value);
      btnRow.appendChild(btn);
    }
    dialog.appendChild(btnRow);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Keyboard handling
    if (input) {
      input.focus();
      input.select();
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          const primary = btnRow.querySelector<HTMLButtonElement>('.primary');
          primary?.click();
        } else if (e.key === 'Escape') {
          // Click the last button (conventionally Cancel)
          const last = btnRow.querySelectorAll<HTMLButtonElement>('.gw-btn');
          last[last.length - 1]?.click();
        }
      });
    } else {
      // Focus the primary button
      const primary = btnRow.querySelector<HTMLButtonElement>('.primary');
      primary?.focus();

      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          window.removeEventListener('keydown', onKey);
          const all = btnRow.querySelectorAll<HTMLButtonElement>('.gw-btn');
          all[all.length - 1]?.click();
        }
      };
      window.addEventListener('keydown', onKey, { once: true });
    }
  });
}

// ---------------------------------------------------------------------------
// Public dialog class
// ---------------------------------------------------------------------------

export class SaveDialog {
  /**
   * Ask the user whether to save results before loading a new STL.
   * Returns true → user wants to save.
   */
  async askWantToSave(prosthesisName: string): Promise<boolean> {
    const { choice } = await showModal(
      'Change of prosthesis',
      `There are analysis results for "${prosthesisName}". Save them before loading a new prosthesis?`,
      [
        { label: 'Save', value: 'yes', style: 'primary' },
        { label: "Don't save", value: 'no', style: 'secondary' },
      ],
    );
    return choice === 'yes';
  }

  /**
   * Ask whether to create a new Excel file or append to an existing one.
   */
  async askCreateOrAppend(): Promise<'create' | 'append' | 'cancel'> {
    const { choice } = await showModal(
      'Save data',
      'Create a new Excel file or add the data to an existing one?',
      [
        { label: 'Create new', value: 'create', style: 'primary' },
        { label: 'Add to existing', value: 'append', style: 'secondary' },
        { label: 'Cancel', value: 'cancel', style: 'danger' },
      ],
    );
    return choice as 'create' | 'append' | 'cancel';
  }

  /**
   * Writing into the chosen local file failed (usually because it is open in Excel).
   * The button click also gives the page a fresh user activation, needed to ask
   * the browser for write permission again.
   */
  async askWriteFailed(fileName: string, reason: string): Promise<'retry' | 'download' | 'cancel'> {
    const { choice } = await showModal(
      'Could not save to the file',
      `Could not write to "${fileName}". If it is open in Excel, close it and press "Retry". ` +
      `The file has not been changed. (${reason})`,
      [
        { label: 'Retry', value: 'retry', style: 'primary' },
        { label: 'Download a copy', value: 'download', style: 'secondary' },
        { label: 'Cancel', value: 'cancel', style: 'danger' },
      ],
    );
    return choice as 'retry' | 'download' | 'cancel';
  }

  /** Browser without File System Access API: the existing file cannot be modified in place. */
  async askDownloadInstead(fileName: string): Promise<boolean> {
    const { choice } = await showModal(
      'This browser cannot modify local files',
      `Firefox and Safari do not let a web page modify "${fileName}" directly, so only an updated copy ` +
      `can be downloaded. To save into the same file use Chrome or Edge.`,
      [
        { label: 'Download a copy', value: 'download', style: 'primary' },
        { label: 'Cancel', value: 'cancel', style: 'secondary' },
      ],
    );
    return choice === 'download';
  }

  /**
   * Ask the user for the name of the new file.
   * Returns null if the user cancels.
   */
  async askFileName(defaultName: string): Promise<string | null> {
    const { choice, inputValue } = await showModal(
      'File name',
      'Enter the name of the Excel file (without extension):',
      [
        { label: 'Save', value: 'ok', style: 'primary' },
        { label: 'Cancel', value: 'cancel', style: 'secondary' },
      ],
      { placeholder: 'file_name', defaultValue: defaultName },
    );
    if (choice === 'cancel' || !inputValue?.trim()) return null;
    return inputValue.trim();
  }

  /**
   * Ask the user to pick an existing .xlsx file.
   *
   * Uses the File System Access API when available (Chrome/Edge) so the returned
   * FileSystemFileHandle can later be used for an in-place write (no download).
   * Falls back to a hidden <input type="file"> on unsupported browsers.
   *
   * Returns null if the user cancels.
   */
  async askPickExistingFile(): Promise<{ file: File; handle?: FileSystemFileHandle } | null> {
    // --- File System Access API (Chrome / Edge) ---
    if ('showOpenFilePicker' in window) {
      try {
        const [handle]: FileSystemFileHandle[] = await (window as any).showOpenFilePicker({
          types: [
            {
              description: 'Excel',
              accept: {
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
              },
            },
          ],
          multiple: false,
        });
        const file: File = await handle.getFile();
        return { file, handle };
      } catch {
        // User closed the native picker
        return null;
      }
    }

    // --- Fallback: hidden <input type="file"> ---
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx';

      let resolved = false;
      const done = (value: { file: File; handle?: FileSystemFileHandle } | null) => {
        if (!resolved) { resolved = true; resolve(value); }
      };

      input.onchange = () => {
        const f = input.files?.[0];
        done(f ? { file: f } : null);
      };

      // When the OS file dialog closes without a selection the window regains
      // focus — use that to resolve null after a short delay.
      window.addEventListener(
        'focus',
        () => setTimeout(() => done(null), 400),
        { once: true },
      );

      input.click();
    });
  }

  /**
   * Open a native Save File Picker (File System Access API).
   * Allows the user to choose an exact location to write the new file in-place.
   *
   * Returns the FileSystemFileHandle on success, or null when:
   *  - The API is not available (Firefox, Safari) → caller should fall back to download.
   *  - The user cancels the picker.
   */
  async askSaveFilePicker(suggestedName: string): Promise<FileSystemFileHandle | null> {
    if (!('showSaveFilePicker' in window)) return null;
    try {
      const handle: FileSystemFileHandle = await (window as any).showSaveFilePicker({
        suggestedName: suggestedName.endsWith('.xlsx') ? suggestedName : `${suggestedName}.xlsx`,
        types: [
          {
            description: 'Excel',
            accept: {
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            },
          },
        ],
      });
      return handle;
    } catch {
      return null;
    }
  }

  /**
   * Inform the user that the prosthesis name already exists and ask whether
   * to overwrite its data or skip saving.
   */
  /** Plain information dialog (OK only). */
  async showInfo(title: string, message: string): Promise<void> {
    await showModal(title, message, [{ label: 'OK', value: 'ok', style: 'primary' }]);
  }

  /** Batch analysis: confirm the list of pairs found and whether to save a wear map per piece. */
  async askBatchStart(summary: string, canSaveMaps: boolean): Promise<'run' | 'run-maps' | 'cancel'> {
    const buttons: { label: string; value: string; style: 'primary' | 'secondary' | 'danger' }[] = [
      { label: 'Analyse', value: 'run', style: 'primary' },
    ];
    if (canSaveMaps) buttons.push({ label: 'Analyse and save PNG wear maps', value: 'run-maps', style: 'secondary' });
    buttons.push({ label: 'Cancel', value: 'cancel', style: 'danger' });
    const { choice } = await showModal('Batch analysis', summary, buttons);
    return choice as 'run' | 'run-maps' | 'cancel';
  }

  /**
   * Case data for the HipCreep export that HipWear does not otherwise know: days between
   * explantation and scanning, and polyethylene grade. Returns null if cancelled.
   */
  async askHipCreepInfo(
    defaults: { daysExVivo: number | null; material: string; radiationDoseKGy?: number | null; thermalTreatment?: string | null },
    materials: { value: string; label: string }[],
    yearsInVivo: number,
    thermals: { value: string; label: string }[] = [],
  ): Promise<{ daysExVivo: number | null; material: string; radiationDoseKGy: number | null; thermalTreatment: string | null } | null> {
    injectStyles();
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'gw-overlay';
      const dialog = document.createElement('div');
      dialog.className = 'gw-dialog';
      const h3 = document.createElement('h3');
      h3.textContent = 'Export HipCreep data';
      dialog.appendChild(h3);
      const p = document.createElement('p');
      p.textContent = 'Creates the input file for HipCreep (creep estimation and creep / wear separation): '
        + 'original sphere, penetration and its uncertainty, cup axis, wall thickness and the trimmed inner surface.';
      dialog.appendChild(p);

      const field = (labelText: string, control: HTMLElement) => {
        const row = document.createElement('div');
        row.className = 'gw-field';
        const lab = document.createElement('label');
        lab.textContent = labelText;
        row.appendChild(lab);
        row.appendChild(control);
        dialog.appendChild(row);
      };
      const days = document.createElement('input');
      days.type = 'number'; days.min = '0'; days.step = '1';
      days.placeholder = 'unknown';
      days.value = defaults.daysExVivo != null ? String(defaults.daysExVivo) : '';
      field('Days from explantation to scan', days);
      const mat = document.createElement('select');
      for (const m of materials) {
        const opt = document.createElement('option');
        opt.value = m.value; opt.textContent = m.label;
        mat.appendChild(opt);
      }
      mat.value = materials.some((m) => m.value === defaults.material) ? defaults.material : materials[0].value;
      field('Polyethylene', mat);
      // radiation dose + thermal treatment (only for the "dose" grade)
      const dose = document.createElement('input');
      dose.type = 'number'; dose.min = '0'; dose.step = '1'; dose.placeholder = 'kGy';
      dose.value = defaults.radiationDoseKGy != null ? String(defaults.radiationDoseKGy) : '';
      field('Total radiation dose (kGy)', dose);
      const th = document.createElement('select');
      for (const t of thermals) {
        const opt = document.createElement('option');
        opt.value = t.value; opt.textContent = t.label;
        th.appendChild(opt);
      }
      if (defaults.thermalTreatment) th.value = defaults.thermalTreatment;
      field('Thermal treatment after irradiation', th);
      const doseRows = [dose.parentElement!, th.parentElement!];
      const syncDose = () => { const on = mat.value === 'dose'; doseRows.forEach((r) => { r.style.display = on ? '' : 'none'; }); };
      mat.addEventListener('change', syncDose);
      syncDose();
      const note = document.createElement('div');
      note.className = 'gw-note';
      note.textContent = yearsInVivo > 0
        ? `Time in vivo: ${yearsInVivo} years (from "Years in vivo").`
        : 'Time in vivo is not set ("Years in vivo" = 0): it can be entered later in HipCreep.';
      dialog.appendChild(note);

      const btnRow = document.createElement('div');
      btnRow.className = 'gw-btns';
      const close = (ok: boolean) => {
        document.body.removeChild(overlay);
        if (!ok) { resolve(null); return; }
        const d = parseFloat(days.value);
        const g = parseFloat(dose.value);
        const isDose = mat.value === 'dose';
        resolve({
          daysExVivo: Number.isFinite(d) && d >= 0 ? d : null, material: mat.value,
          radiationDoseKGy: isDose && Number.isFinite(g) && g >= 0 ? g : null,
          thermalTreatment: isDose ? (th.value || 'none') : null,
        });
      };
      const bOk = document.createElement('button');
      bOk.className = 'gw-btn primary'; bOk.textContent = 'Export';
      bOk.onclick = () => close(true);
      const bCancel = document.createElement('button');
      bCancel.className = 'gw-btn secondary'; bCancel.textContent = 'Cancel';
      bCancel.onclick = () => close(false);
      btnRow.appendChild(bOk); btnRow.appendChild(bCancel);
      dialog.appendChild(btnRow);
      overlay.appendChild(dialog);
      document.body.appendChild(overlay);
      days.focus();
      dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); bOk.click(); }
        else if (e.key === 'Escape') { e.preventDefault(); bCancel.click(); }
      });
    });
  }

  async askOverwriteOrSkip(prosthesisName: string): Promise<'overwrite' | 'skip'> {
    const { choice } = await showModal(
      'Prosthesis already exists',
      `There is already an entry for "${prosthesisName}" in the file. Replace its data?`,
      [
        { label: 'Replace', value: 'overwrite', style: 'primary' },
        { label: 'Cancel', value: 'skip', style: 'secondary' },
      ],
    );
    return choice as 'overwrite' | 'skip';
  }
}
